import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CreditCard, ExternalLink, ArrowLeft, Check, ShieldCheck, Tag, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';
import { trackGoogleAdsConversion } from '@/lib/google-ads';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const PLANS_FALLBACK = {
  basico: { name: 'Básico', price: '29,90', features: ['Até 15 avaliações/mês', 'Dashboard Completo', 'Respostas via WhatsApp e E-mail', 'Alertas de Crise no WhatsApp'] },
  pro: { name: 'Pro', price: '39,90', features: ['Até 30 avaliações/mês', 'Logo Própria no QR Code', 'PDF A4 para Impressão', 'Respostas via WhatsApp e E-mail'] },
  enterprise: { name: 'Empresarial', price: '59,90', features: ['Até 60 avaliações/mês', 'Tudo do Pro', 'Suporte Prioritário', 'Relatórios PDF e Excel'] },
  elite: { name: 'Elite', price: '99,90', features: ['Até 100 avaliações/mês', 'Tudo do Empresarial', 'Alerta de Crise (Nota Baixa)', 'Notificação Instantânea por E-mail'] },
  five_stars: { name: '5 Estrelas', price: '149,90', features: ['Avaliações ILIMITADAS', 'Tudo do Elite', 'Automação Google Maps', 'Convite automático para avaliação 5 estrelas no Google'] },
};

const PublicCheckout = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState(PLANS_FALLBACK[planId] || null);
  const [step, setStep] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const pollRef = useRef(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', cpf_cnpj: '', company_name: ''
  });
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    // Load dynamic plan prices
    axios.get(`${API}/public/plan-configs`).then(res => {
      const cfg = res.data.find(p => p.plan_id === planId);
      if (cfg) {
        setPlan(prev => ({
          ...prev,
          name: cfg.name,
          price: cfg.price.toFixed(2).replace('.', ','),
          numericPrice: cfg.price,
          limit: cfg.limit
        }));
      }
    }).catch((e) => { console.error('Erro ao carregar dados do plano:', e); });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [planId]);

  // Auto-aplicar cupom vindo da URL (?cupom=VOLTEI10) — usado em campanhas de remarketing
  useEffect(() => {
    const urlCoupon = searchParams.get('cupom') || searchParams.get('coupon');
    if (!urlCoupon || couponApplied) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/coupons/validate`, { code: urlCoupon.trim().toUpperCase(), plan_id: planId });
        if (cancelled) return;
        setCouponCode(urlCoupon.toUpperCase());
        setCouponApplied(res.data);
        toast.success(`Cupom ${res.data.code} aplicado automaticamente!`, { duration: 5000 });
      } catch {
        // Cupom da URL invalido — silencioso, nao polui UX
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, searchParams]);

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <p className="text-slate-600 mb-4">Plano não encontrado.</p>
            <Link to="/"><Button className="btn-primary">Voltar à página inicial</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode.trim(), plan_id: planId });
      setCouponApplied(res.data);
      toast.success(`Cupom ${res.data.code} aplicado! Desconto de R$ ${res.data.discount_amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cupom invalido');
      setCouponApplied(null);
    } finally { setValidatingCoupon(false); }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
  };

  const formatCpfCnpj = (v) => {
    const d = v.replace(/\D/g, '');
    if (d.length <= 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = form.cpf_cnpj.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      toast.error('CPF ou CNPJ inválido');
      return;
    }

    setSubmitting(true);
    setStep('processing');
    trackEvent('checkout_started', { plan: planId });
    trackGoogleAdsConversion('checkout_started', { value: plan?.price || 0, transaction_id: `checkout_${Date.now()}` });
    try {
      // Read referral cookie for affiliate attribution
      const refCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_affiliate='));
      const referred_by_raw = refCookie ? refCookie.split('=')[1].trim() : undefined;

      // Read owner referral cookie (Indique e Ganhe)
      const refOwnerCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_owner='));
      const referred_by_owner = refOwnerCookie ? refOwnerCookie.split('=')[1].trim() : undefined;

      // Read partner cookie (Parceiro Recorrente)
      const refPartnerCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_partner='));
      const partner_slug = refPartnerCookie ? refPartnerCookie.split('=')[1].trim() : undefined;

      // REGRA: Enviar ambos — o backend decide se ambos ganham (afiliado vinculado a parceiro)
      // ou se parceiro prevalece (afiliado sem vínculo)
      const referred_by = referred_by_raw;

      const res = await axios.post(`${API}/payments/public-checkout`, {
        plan_id: planId,
        name: form.name,
        email: form.email,
        cpf_cnpj: digits,
        phone: form.phone,
        company_name: form.company_name,
        referred_by,
        referred_by_owner,
        partner_slug,
        coupon_code: couponApplied ? couponApplied.code : undefined
      });

      setPaymentData(res.data);

      // If Asaas failed but company was created, redirect to success immediately
      if (res.data.asaas_pending) {
        clearInterval(pollRef.current);
        navigate(`/obrigado?session=${res.data.session_id}`);
        return;
      }

      setStep('payment');

      pollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/checkout/session/${res.data.session_id}`);
          if (st.data.status === 'completed') {
            clearInterval(pollRef.current);
            navigate(`/obrigado?session=${res.data.session_id}`);
          }
        } catch (err) { console.warn('Polling: aguardando confirmação...', err.message); }
      }, 5000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar. Tente novamente.');
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2647] relative" data-testid="public-checkout-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#C5A059]/[0.04] rounded-full blur-[150px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#C5A059]/[0.06] rounded-full blur-[120px]" />
      </div>

      <header className="relative z-10 border-b border-white/5 py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-12 w-auto object-contain" />
            <span className="font-bold text-lg text-white hidden sm:block" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono</span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-white flex items-center gap-1" data-testid="checkout-back-link">
            <ArrowLeft className="w-4 h-4" />Voltar
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {step === 'form' && (
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-2xl p-6 sticky top-24">
                <p className="text-xs font-semibold text-[#C5A059] uppercase tracking-wider mb-3">Plano Selecionado</p>
                <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>{plan.name}</h2>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>R$ {plan.price}</span>
                  <span className="text-slate-400 text-sm">/mês</span>
                </div>
                <ul className="space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-[#C5A059] mt-0.5 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
                  <ShieldCheck className="w-4 h-4 text-[#C5A059]" />Pagamento 100% seguro via Asaas
                </div>
                {/* Coupon applied summary */}
                {couponApplied && (
                  <div className="mt-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-emerald-400 flex items-center gap-1"><Tag className="w-3 h-3" />Cupom: {couponApplied.code}</span>
                      <button onClick={removeCoupon} className="text-slate-400 hover:text-white"><X className="w-3 h-3" /></button>
                    </div>
                    <div className="text-xs text-slate-400 space-y-0.5">
                      <div className="flex justify-between"><span>Original:</span><span className="line-through">R$ {couponApplied.original_price.toFixed(2)}</span></div>
                      <div className="flex justify-between text-emerald-400"><span>Desconto:</span><span>-R$ {couponApplied.discount_amount.toFixed(2)}</span></div>
                      <div className="flex justify-between text-white font-bold text-sm pt-1 border-t border-white/10 mt-1"><span>Final:</span><span className="text-[#C5A059]">R$ {couponApplied.final_price.toFixed(2)}/mes</span></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-3">
              <Card className="shadow-2xl border-0">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-slate-900">Dados para Assinatura</CardTitle>
                  <CardDescription>Preencha seus dados para ativar o plano {plan.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Seu Nome Completo</Label>
                      <Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} required className="h-11" placeholder="João Silva" data-testid="checkout-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">E-mail</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} required className="h-11" placeholder="seu@email.com" data-testid="checkout-email" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">WhatsApp</Label>
                      <Input value={form.phone} onChange={(e) => setForm(p => ({...p, phone: e.target.value.replace(/\D/g, '')}))} className="h-11" placeholder="5531999999999" data-testid="checkout-phone" />
                      <p className="text-[10px] text-slate-400 mt-0.5">Formato: 55 + DDD + número</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">CPF ou CNPJ</Label>
                      <Input value={form.cpf_cnpj} onChange={(e) => setForm(p => ({...p, cpf_cnpj: formatCpfCnpj(e.target.value)}))} required maxLength={18} className="h-11" placeholder="000.000.000-00" data-testid="checkout-cpf" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Nome da sua Empresa</Label>
                      <Input value={form.company_name} onChange={(e) => setForm(p => ({...p, company_name: e.target.value}))} required className="h-11" placeholder="Padaria do João" data-testid="checkout-company" />
                    </div>
                    {/* Cupom de desconto */}
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      <Label className="text-sm font-medium flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5 text-purple-500" />Possui cupom de desconto?
                      </Label>
                      {couponApplied ? (
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-bold text-emerald-700">{couponApplied.code}</span>
                          <span className="text-xs text-emerald-600">(-R$ {couponApplied.discount_amount.toFixed(2)})</span>
                          <button onClick={removeCoupon} className="ml-auto text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={couponCode}
                            onChange={e => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Ex: FILIAL30"
                            className="h-11 font-mono uppercase"
                            data-testid="checkout-coupon"
                          />
                          <Button
                            type="button"
                            onClick={validateCoupon}
                            disabled={validatingCoupon || !couponCode.trim()}
                            variant="outline"
                            className="h-11 px-4 whitespace-nowrap"
                            data-testid="validate-coupon-btn"
                          >
                            {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                          </Button>
                        </div>
                      )}
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold text-base rounded-xl mt-2" data-testid="checkout-submit">
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processando...</> : couponApplied ? `Pagar R$ ${couponApplied.final_price.toFixed(2)}/mês` : 'Ir para o Pagamento'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="max-w-md mx-auto text-center py-20">
            <Loader2 className="w-12 h-12 text-[#C5A059] animate-spin mx-auto mb-6" />
            <h2 className="text-xl font-bold text-white mb-2">Preparando seu pagamento...</h2>
            <p className="text-slate-400 text-sm">Aguarde enquanto geramos sua fatura.</p>
          </div>
        )}

        {step === 'payment' && paymentData && (
          <div className="max-w-lg mx-auto">
            <Card className="shadow-2xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-[#0A2647] to-[#0D3158] text-white text-center">
                <CreditCard className="w-8 h-8 text-[#C5A059] mx-auto mb-2" />
                <CardTitle className="text-lg">Pagamento - Plano {paymentData.plan_name}</CardTitle>
                <CardDescription className="text-slate-300">R$ {paymentData.value?.toFixed(2).replace('.', ',')}/mês - Recorrente</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-5">
                {paymentData.invoice_url && (
                  <a href={paymentData.invoice_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-4 px-4 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] rounded-xl font-bold text-base transition-colors"
                    data-testid="checkout-pay-link">
                    <ExternalLink className="w-5 h-5" />
                    Pagar com PIX, Cartão ou Boleto
                  </a>
                )}
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 bg-amber-50 py-3 px-4 rounded-lg" data-testid="checkout-waiting">
                  <Loader2 className="w-4 h-4 animate-spin" />Aguardando confirmação do pagamento...
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Após o pagamento, suas credenciais de acesso serão geradas automaticamente.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicCheckout;
